import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import lodash from 'lodash';

import {
  Button,
  Grid,
  GridItem,
  Heading,
  Icons,
  LineChart,
  PendingContent,
  Space,
  Spinner,
  Status,
  TableV2,
} from 'plume-ui';
import { HeadingStyles } from 'plume-ui/dist/components/Heading/Heading';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useRecoilValue } from 'recoil';
import { activePartnerAtom } from '../../../state';
import { getFrontlineUrl } from '../../../utils/environments';
import InfoCard, {
  InfoCardGridItem,
} from '../components/AutonomousMonitorDetails/InfoCard';
import {
  generateDNSLineChartData,
  generateFakeLineChartData,
  randomLocationId,
} from './utils';

dayjs.extend(utc);

const MONITORS_DATA = {
  ont: {
    name: 'ONT',
    affectedLocations: 0,
    graphData: generateFakeLineChartData(24, 0),
    tableData: [],
    criteria: 'ONT signal strength below -28 dBm for more than 15 minutes',
  },
  olt: {
    name: 'OLT',
    affectedLocations: 0,
    graphData: generateFakeLineChartData(24, 0),
    tableData: [],
    criteria: 'OLT port experiencing more than 20% packet loss over 10 minutes',
  },
  dhcp: {
    name: 'DHCP',
    affectedLocations: 1000,
    graphData: generateFakeLineChartData(24, 1000, 900),
    tableData: Array.from({ length: 200 }, (_) => ({
      location_id: randomLocationId(),
      health_score: lodash.random(80, 100),
    })),
    criteria: 'DHCP request failure rate exceeding 25% in 5 minutes',
  },
  dns: {
    name: 'DNS resolution failures',
    affectedLocations: 20311,
    graphData: generateDNSLineChartData(24),
    tableData: Array.from({ length: 200 }, (_) => ({
      location_id: randomLocationId(),
      health_score: lodash.random(80, 100),
    })),
    criteria: `DNS more than 30% failures in 5 minutes`,
  },
  conf: {
    name: 'Configuration failures',
    affectedLocations: 5,
    graphData: generateFakeLineChartData(24, 5, 5),
    tableData: [],
    criteria: 'Configuration failure rate exceeding 25% in 5 minutes',
  },
  port: {
    name: 'Port flapping',
    affectedLocations: 0,
    graphData: generateFakeLineChartData(24, 0),
    tableData: [],
    criteria: 'Port flapping failure rate exceeding 25% in 5 minutes',
  },
  voip: {
    name: 'VoIP QoE',
    affectedLocations: 300,
    graphData: generateFakeLineChartData(24, 300, 250),
    tableData: [],
    criteria: 'VoIP QoE issues exceeding 1000 5 minutes',
  },
  service: {
    name: 'Service outages',
    affectedLocations: 0,
    graphData: generateFakeLineChartData(24, 0),
    tableData: [],
    criteria: 'Service outages failure rate exceeding 25% in 5 minutes',
  },
  lastmile: {
    name: 'Last mile degradation',
    affectedLocations: 600,
    graphData: generateFakeLineChartData(24, 600, 500),
    tableData: [],
    criteria: 'Last mile degradation failure rate exceeding 25% in 5 minutes',
  },
};

const AccessNetworkMonitorDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { monitorId } = useParams<{ monitorId: string }>();

  const [loading, setLoading] = useState(true);

  const activePartner = useRecoilValue(activePartnerAtom);

  const activeMonitor = MONITORS_DATA[monitorId as keyof typeof MONITORS_DATA];

  const graphGroups = useMemo(
    () => [
      {
        label: t('monitors.monitorInfo.unresolvedLocations.title'),
        stroke: '#656cff',
        key: 'value',
      },
    ],
    [t],
  );

  const infoGridData: InfoCardGridItem[] = [
    {
      label: t('monitors.autonomous.monitorDetails.monitoredIssue'),
      value: activeMonitor?.name,
    },
    {
      label: t('monitors.monitorInfo.unresolvedLocations.title'),
      value: activeMonitor?.affectedLocations,
    },

    {
      label: t('monitors.monitorInfo.monitorInfoMetrics.monitorCriteria'),
      value: activeMonitor?.criteria,
    },

    {
      label: t('monitors.monitorInfo.supportOutreach'),
      value: (
        <div className="AutonomousMonitorDetails__outreachStatus">
          <Status label={t(`monitors.outreachStatuses.active`)} color={'ok'} />
        </div>
      ),
    },
    {
      label: t('monitors.monitorInfo.webhookOutreach'),
      value: (
        <div className="AutonomousMonitorDetails__outreachStatus">
          <Status label={t(`monitors.outreachStatuses.active`)} color={'ok'} />
        </div>
      ),
    },
    {
      label: t('monitors.monitorInfo.subscriberOutreach'),
      value: (
        <div className="AutonomousMonitorDetails__outreachStatus">
          <Status label={t(`monitors.outreachStatuses.active`)} color={'ok'} />
        </div>
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });

    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  return (
    <div className="AutonomousMonitorDetails">
      <div className="AutonomousMonitorDetails__header">
        <Button
          onClick={() => {
            history.goBack();
          }}
          styleVariant="tertiary-grey"
        >
          <Icons.ArrowLeftIcon width={16} />
          {t('actions.back')}
        </Button>
      </div>

      <Grid>
        <GridItem colSpan="12" tabletColSpan="12">
          <Space size="l" />
          <Heading
            classes={(current: HeadingStyles) => ({
              root: `${current.root} AppLayout__header AutonomousMonitorDetails__title`,
            })}
            size="xxxxl"
            medium
          >
            {activeMonitor?.name}
          </Heading>
        </GridItem>
        <GridItem colSpan="12" tabletColSpan="12">
          <PendingContent hideContent loading={loading} loader={Spinner}>
            <img
              src={'/images/png/accessNetwork.png'}
              alt="Access Network"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                maxHeight: 400,
                filter: 'contrast(1.2)',
              }}
            />
          </PendingContent>
        </GridItem>

        <GridItem colSpan="12" tabletColSpan="12">
          <PendingContent hideContent loading={loading} loader={Spinner}>
            <InfoCard label="Affected locations">
              <div style={{ height: '300px', overflow: 'auto' }}>
                <TableV2
                  data={activeMonitor.tableData}
                  stickyHeader
                  rowKey={(monitor) => monitor.location_id}
                  columns={[
                    {
                      key: 'location_id',
                      dataIndex: 'location_id',
                      title: 'Location ID',
                    },
                    {
                      key: 'account_id',
                      dataIndex: 'account_id',
                      title: 'Account ID',
                      render: () => activePartner?.id,
                    },
                    {
                      key: 'health_score',
                      dataIndex: 'health_score',
                      title: 'Health Score',
                    },
                    {
                      key: 'actions',
                      title: 'Actions',
                      render: () => (
                        <Button
                          styleVariant="tertiary"
                          onClick={() => {
                            window.open(
                              `${getFrontlineUrl()}/location/670063d2cee564249dc2987b `,
                              '_blank',
                            );
                          }}
                        >
                          {t(
                            'monitors.monitorInfo.unresolvedLocations.frontline',
                          )}
                        </Button>
                      ),
                    },
                  ]}
                />
              </div>
            </InfoCard>
          </PendingContent>
        </GridItem>

        <GridItem colSpan="12" tabletColSpan="12">
          <PendingContent hideContent loading={loading} loader={Spinner}>
            <InfoCard
              label={t('monitors.monitorInfo.monitorInfo')}
              gridData={infoGridData}
            />
          </PendingContent>
        </GridItem>
        <GridItem colSpan="12" tabletColSpan="12">
          <PendingContent hideContent loading={loading} loader={Spinner}>
            <InfoCard
              label={t('monitors.autonomous.monitorDetails.monitorTrends')}
              headerRightContent={t('monitors.durationFilters.last24hrs')}
            >
              <LineChart
                height={300}
                data={activeMonitor?.graphData || []}
                statusVariant="legend"
                legendPosition="bottom"
                groups={graphGroups}
              />
            </InfoCard>
          </PendingContent>
        </GridItem>
      </Grid>

      <Space size="l" />
    </div>
  );
};

export default memo(AccessNetworkMonitorDetails);
